/**
 * Campaign merge-fields for text & heading blocks.
 *
 * Lets a campaign owner drop LIVE campaign numbers into any text/heading block
 * — סכום שנאסף, מספר תרומות, הוראות קבע, יעד … — plus their own calculation
 * (a Formula, the same structured no-eval data the progress meter uses).
 *
 * Storage: blocks keep RAW tokens in `block.content` (`{c:raised}`, `{c:f:<id>}`)
 * exactly like the email builder keeps `{name}`. Per-block formulas live in
 * `block.props.textFormulas` keyed by id. Two render paths consume them:
 *   - BUILDER preview  → decorateCampaignDisplay(): tokens become labelled chips
 *   - PUBLIC page      → substituteCampaignTokens(): tokens become formatted values
 *
 * The chip DISPLAY layer is shared with the email builder (mergeTagChips), so a
 * `{c:…}` token renders as the same pill the user already knows from emails.
 */
import { Formula, evalFormula, formatFormula } from './formula';
import { chipSpanHtml } from '../MailBuilder/mergeTagChips';
import { sanitizeInlineHtml, literalBrToNewline } from './utils';
import { currencySymbol } from '../../lib/formatUtils';

export interface MergeField {
  token: string;
  label: string;
  group?: string;
}

/** The live values needed to resolve a campaign token. Resolved per active
 *  currency by the caller (PublicCampaign). */
export interface CampaignTokenCtx {
  raised: number;
  donationCount: number;
  recurringCount: number;
  goal: number;
  /** ISO currency code (ILS/USD/EUR/GBP) — only used to pick the symbol. */
  currency?: string;
  /** Per-block formulas, keyed by id (block.props.textFormulas). */
  formulas?: Record<string, Formula>;
}

const FIELD_GROUP = 'נתוני קמפיין';

/** The fixed campaign metric tokens (everything except per-block formulas). */
export const CAMPAIGN_METRIC_FIELDS: MergeField[] = [
  { token: '{c:raised}', label: 'סכום שנאסף', group: FIELD_GROUP },
  { token: '{c:donationCount}', label: 'מספר תרומות', group: FIELD_GROUP },
  { token: '{c:recurringCount}', label: 'הוראות קבע', group: FIELD_GROUP },
  { token: '{c:goal}', label: 'יעד', group: FIELD_GROUP },
  { token: '{c:goalPct}', label: 'אחוז מהיעד', group: FIELD_GROUP },
  { token: '{c:remaining}', label: 'נותר ליעד', group: FIELD_GROUP },
];

export function formulaToken(id: string): string {
  return `{c:f:${id}}`;
}

const FORMULA_TOKEN_RE = /\{c:f:([A-Za-z0-9_-]+)\}/g;

/**
 * The full field list for chip decoration / the picker: the fixed metrics plus
 * one entry per stored formula (label = its human rendering, e.g. "סכום תרומות ÷ 45").
 */
export function buildCampaignFields(formulas?: Record<string, Formula>): MergeField[] {
  const formulaFields: MergeField[] = Object.entries(formulas || {}).map(([id, f]) => ({
    token: formulaToken(id),
    label: formatFormula(f),
    group: 'נוסחאות',
  }));
  return [...CAMPAIGN_METRIC_FIELDS, ...formulaFields];
}

function escHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** A regex matching any of the given tokens (longest first), or null if none. */
function tokenRegex(fields: MergeField[]): RegExp | null {
  const pattern = fields
    .map((f) => f.token)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map(escRegExp)
    .join('|');
  return pattern ? new RegExp(pattern, 'g') : null;
}

/**
 * Builder-preview decoration: turn stored content into display HTML with
 * labelled chips. Rich content is sanitised with the campaign's own inline
 * sanitiser (so the preview matches the public render); plain content is
 * escaped (caller keeps white-space: pre-wrap so newlines survive). Only known
 * tokens become chips — unknown `{…}` stays literal.
 */
export function decorateCampaignDisplay(content: string, fields: MergeField[], isRich: boolean): string {
  let html = isRich ? sanitizeInlineHtml(content || '') : escHtml(literalBrToNewline(content || ''));
  const re = tokenRegex(fields);
  if (re) {
    const labelByToken = new Map(fields.map((f) => [f.token, f.label]));
    html = html.replace(re, (m) => chipSpanHtml(m, labelByToken.get(m) || m));
  }
  return html;
}

/**
 * Public-render substitution: replace every `{c:…}` token in stored content
 * with its live, formatted value. Money metrics get the active-currency symbol;
 * counts and percentages format per locale. Formula tokens are evaluated against
 * the same live metrics (the formula's own rounding mode already applied inside
 * evalFormula). Unknown formula ids resolve to empty string. Content with no
 * `{c:` substring is returned untouched (cheap fast-path).
 */
export function substituteCampaignTokens(
  content: string,
  ctx: CampaignTokenCtx,
  locale: 'he' | 'en' = 'he',
): string {
  if (!content || content.indexOf('{c:') === -1) return content;
  const loc = locale === 'en' ? 'en-US' : 'he-IL';
  const sym = currencySymbol(ctx.currency || 'ILS');
  const metrics = {
    raised: ctx.raised || 0,
    donationCount: ctx.donationCount || 0,
    recurringCount: ctx.recurringCount || 0,
    goal: ctx.goal || 0,
  };
  const goal = ctx.goal || 0;
  const pct = goal > 0 ? Math.round((metrics.raised / goal) * 100) : 0;
  const remaining = Math.max(0, goal - metrics.raised);
  const money = (n: number) => `${sym}${Math.round(n).toLocaleString(loc)}`;
  const count = (n: number) => Math.round(n).toLocaleString(loc);

  let out = content
    .split('{c:raised}').join(money(metrics.raised))
    .split('{c:donationCount}').join(count(metrics.donationCount))
    .split('{c:recurringCount}').join(count(metrics.recurringCount))
    .split('{c:goal}').join(money(goal))
    .split('{c:goalPct}').join(`${pct}%`)
    .split('{c:remaining}').join(money(remaining));

  out = out.replace(FORMULA_TOKEN_RE, (_m, id) => {
    const f = ctx.formulas && ctx.formulas[id];
    if (!f) return '';
    const v = evalFormula(f, metrics);
    return Number.isInteger(v) ? v.toLocaleString(loc) : v.toLocaleString(loc, { maximumFractionDigits: 2 });
  });
  return out;
}
